// watch/CreatorRow.tsx — the uploader under the title (1.27.50, owner concept): avatar,
// name, verified mark, handle, Follow, and — once you follow — the bell, which is the
// existing per-creator "notify me of new posts" switch (Settings → Notifications lists the
// same switch for every creator you follow).
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Avatar, Button, IconButton, useToast } from '@/ui';
import { Icon } from '@/icons';
import { followButtonLabel, useFollow, useRequireAuth, useSession } from '@/hooks';
import { useWriteGate } from '@/hooks/writeGate';
import { followsService } from '@/core/services/followsService';
import type { Post } from '@/core/models/post';

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 14px;
`;

const Who = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  text-decoration: none;
  color: ${({ theme }) => theme.color.textPrimary};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 3px;
    border-radius: 999px;
  }
`;

const Names = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  strong {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font: 700 ${({ theme }) => theme.fontSize.md} ${({ theme }) => theme.font.body};
  }
  small {
    color: ${({ theme }) => theme.color.textMuted};
    font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  }
`;

const Verified = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.color.accentCyan};
`;

export function CreatorRow({ post, isOwn }: { post: Post; isOwn: boolean }) {
  const { isAuthenticated } = useSession();
  const requireAuth = useRequireAuth();
  const gate = useWriteGate();
  const toast = useToast();
  const { isFollowing, isRequested, toggleFollow } = useFollow(post.handle);
  const [notify, setNotify] = useState<boolean | null>(null);

  // The bell's current state comes from the same list Settings → Notifications reads.
  useEffect(() => {
    if (!isAuthenticated || !isFollowing || isOwn) return;
    let live = true;
    followsService
      .followedCreators()
      .then((rows) => {
        if (!live) return;
        const me = rows.find((r) => r.handle.toLowerCase() === post.handle.toLowerCase());
        setNotify(me ? me.notifyPosts : false);
      })
      .catch(() => live && setNotify(false));
    return () => {
      live = false;
    };
  }, [isAuthenticated, isFollowing, isOwn, post.handle]);

  async function flipNotify() {
    const next = !notify;
    setNotify(next);
    try {
      const saved = await followsService.setCreatorNotify(post.handle, next);
      setNotify(saved);
      toast(
        saved ? `You'll be notified of new posts from ${post.handle}` : 'Post notifications off',
      );
    } catch {
      setNotify(!next);
      toast('Something went wrong');
    }
  }

  const handleNoAt = post.handle.replace(/^@/, '');
  return (
    <Row>
      <Who to={`/profile/${handleNoAt}`}>
        <Avatar name={post.name} avatarKey={post.av} size={48} />
        <Names>
          <strong>
            {post.name}
            {post.verified && (
              <Verified aria-label="Verified">
                <Icon name="verify" size={16} />
              </Verified>
            )}
          </strong>
          <small>{post.handle}</small>
        </Names>
      </Who>
      {!isOwn && (
        <Button
          variant={isFollowing || isRequested ? 'secondary' : 'primary'}
          onClick={() => {
            if (requireAuth('follow')) void toggleFollow();
          }}
          aria-label={`${followButtonLabel(isFollowing, isRequested)} ${post.handle}`}
        >
          {followButtonLabel(isFollowing, isRequested)}
        </Button>
      )}
      {!isOwn && isFollowing && notify !== null && (
        <IconButton
          icon={notify ? 'bell-ringing' : 'bell'}
          label={
            notify
              ? `Turn off post notifications from ${post.handle}`
              : `Notify me of new posts from ${post.handle}`
          }
          aria-pressed={notify}
          onClick={() => {
            if (gate.allow('change post notifications')) void flipNotify();
          }}
          size={20}
        />
      )}
    </Row>
  );
}
